import { useState, useEffect } from "react";

/**
 * Custom Hook to preload an image set
 * @param {Object} imageUrls - An object where the keys are identifiers and the values are the URLs of the images to load
 * @param {number} [timeout=10000] - Time in milliseconds to wait before dialing a timeout
 * @returns {{allImagesLoaded: boolean, timeoutReached: boolean}} - An object with two booleans: if all images were loaded and if timeout was reached
 */
export function useImagePreloader(imageUrls, timeout = 10000) {
  const [allImagesLoaded, setAllImagesLoaded] = useState(false);
  const [timeoutReached, setTimeoutReached] = useState(false);

  useEffect(() => {
    // Everything is already loaded or there are no images, don't do anything
    if (allImagesLoaded || !imageUrls || Object.keys(imageUrls).length === 0) {
      setAllImagesLoaded(true);
      return;
    }

    // Reset states in case URLs change
    setAllImagesLoaded(false);
    setTimeoutReached(false);

    const urls = Object.values(imageUrls);
    let loadedCount = 0;

    const handleImageLoad = () => {
      loadedCount++;
      if (loadedCount === urls.length) {
        setAllImagesLoaded(true);
      }
    };

    const imageElements = urls.map((url) => {
      const image = new Image();
      image.src = url;
      image.addEventListener("load", handleImageLoad);
      return image;
    });

    // Cleaning function to remove event listeners and prevent memory leaks
    return () => {
      imageElements.forEach((image) => {
        image.removeEventListener("load", handleImageLoad);
      });
    };
    // JSON.stringify to re-run the effect if the URL object changes
  }, [JSON.stringify(imageUrls)]);

  useEffect(() => {
    // If the images load before the timeout, don't do anything
    if (allImagesLoaded) {
      return;
    }

    // start the timer.
    const timeoutId = setTimeout(() => {
      // If after the time set the images have not yet loaded, mark the timeout was reached
      if (!allImagesLoaded) {
        setTimeoutReached(true);
        console.warn("La precarga de imágenes excedió el tiempo límite.");
      }
    }, timeout);

    // Cleans the timer if the component is unmounted or if the images are loaded on time
    return () => clearTimeout(timeoutId);
  }, [allImagesLoaded, timeout]);

  return { allImagesLoaded, timeoutReached };
}
